/**
 * Freie Zeitfenster eines Tages (F-50) - rein und getestet.
 *
 * Grundlage fuer die Agenda der Uebersicht und spaeter fuer den Auto-Planer
 * (P-03), "Was jetzt?" (P-04) und die Ueberplanungs-Warnung (P-10).
 *
 * Regeln:
 * - Gerechnet wird in Minuten des Tages (0-1440), nur im Tagesrahmen (wach
 *   von-bis). Endet der Rahmen vor seinem Beginn (z. B. 07:00-01:00), gilt er
 *   bis Mitternacht - der Rest gehoert zum naechsten Tag.
 * - Ganztaegige Termine belegen keine Zeit. Ausgefallene (F-29) auch nicht;
 *   "anders gelaufen" belegt die tatsaechliche Zeit.
 * - Termine ueber Mitternacht zaehlen nur mit ihrem Anteil an diesem Tag.
 * - Schulstunden belegen als EIN Block von der ersten bis zur letzten Stunde:
 *   Pausen zwischen den Stunden sind keine nutzbare Zeit. Klausuren sind
 *   einzelne Termine und stehen als eigene Zeile (`istSchulstunde`).
 * - Ueberschneidungen werden zusammengefasst; Fenster unter `min` Minuten
 *   fallen weg.
 */
import { istSchulstunde } from '@/domain/school/ferien';
import { datePart, minutesOfDay } from '@/lib/datetime';
import type { CalendarOccurrence, LocalDate, LocalDateTime } from '@/domain/types';

/** Minuten des Tages, `bis` exklusiv. */
export interface Intervall { von: number; bis: number }

/** Wach von-bis, jeweils `'HH:mm'`. */
export interface Tagesrahmen { von: string; bis: string }

/** Ole steht werktags um 5:00 auf; 21:00 entspricht dem Standardschlaf von 8 h. */
export const STANDARD_TAGESRAHMEN: Tagesrahmen = { von: '05:00', bis: '21:00' };

/** Kuerzere Luecken sind keine nutzbaren Fenster. */
export const MIN_FENSTER = 15;

const TAG = 24 * 60;
const UHRZEIT = /^([01]\d|2[0-3]):[0-5]\d$/;

export const minutenAus = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Gespeicherte (evtl. alte oder kaputte) Werte auf einen gueltigen Rahmen bringen. */
export function tagesrahmenAus(roh: unknown): Tagesrahmen {
  const r = (roh && typeof roh === 'object' ? roh : {}) as Partial<Tagesrahmen>;
  const von = typeof r.von === 'string' && UHRZEIT.test(r.von) ? r.von : STANDARD_TAGESRAHMEN.von;
  const bis = typeof r.bis === 'string' && UHRZEIT.test(r.bis) ? r.bis : STANDARD_TAGESRAHMEN.bis;
  return von === bis ? STANDARD_TAGESRAHMEN : { von, bis };
}

/** Der Rahmen als Intervall; ein Ende vor dem Beginn heisst: bis Mitternacht. */
export function rahmenIntervall(r: Tagesrahmen): Intervall {
  const von = minutenAus(r.von);
  const bis = minutenAus(r.bis);
  return { von, bis: bis > von ? bis : TAG };
}

/** Ueberschneidende oder aneinanderstossende Intervalle zusammenfassen, sortiert. */
export function zusammenfassen(xs: Intervall[]): Intervall[] {
  const sortiert = xs.filter((x) => x.bis > x.von).sort((a, b) => a.von - b.von);
  const out: Intervall[] = [];
  for (const x of sortiert) {
    const letztes = out[out.length - 1];
    if (letztes && x.von <= letztes.bis) letztes.bis = Math.max(letztes.bis, x.bis);
    else out.push({ ...x });
  }
  return out;
}

/** Anteil eines Zeitraums an `tag` in Minuten; `null`, wenn er den Tag nicht beruehrt. */
export function anteilAm(start: LocalDateTime, end: LocalDateTime, tag: LocalDate): Intervall | null {
  const s = datePart(start), e = datePart(end);
  if (s > tag || e < tag) return null;
  const von = s < tag ? 0 : minutesOfDay(start);
  const bis = e > tag ? TAG : minutesOfDay(end);
  return bis > von ? { von, bis } : null;
}

/** Belegt das Vorkommen Zeit? Ganztaegig und ausgefallen nicht. */
const belegtZeit = (o: CalendarOccurrence) => !o.allDay && o.bestaetigung?.status !== 'ausgefallen';

/** Die tatsaechliche Zeit eines Vorkommens: bei "anders gelaufen" die eingetragene. */
function zeitVon(o: CalendarOccurrence): { start: LocalDateTime; end: LocalDateTime } {
  const b = o.bestaetigung;
  if (b?.status === 'anders' && b.start && b.end) return { start: b.start, end: b.end };
  return { start: o.start as LocalDateTime, end: o.end as LocalDateTime };
}


/** Schulstunden des Tages als ein Block (erste bis letzte Stunde) - `null` ohne Stunden. */
export function schulBlock(occ: CalendarOccurrence[], tag: LocalDate): (Intervall & { anzahl: number }) | null {
  const teile = occ.filter((o) => istSchulstunde(o) && belegtZeit(o))
    .flatMap((o) => { const z = zeitVon(o); const a = anteilAm(z.start, z.end, tag); return a ? [a] : []; });
  if (teile.length === 0) return null;
  return { von: Math.min(...teile.map((t) => t.von)), bis: Math.max(...teile.map((t) => t.bis)), anzahl: teile.length };
}

/** Belegte Zeit eines Tages, zusammengefasst (Schule als ein Block). */
export function belegtAm(occ: CalendarOccurrence[], tag: LocalDate): Intervall[] {
  const termine = occ.filter((o) => belegtZeit(o) && !istSchulstunde(o))
    .flatMap((o) => { const z = zeitVon(o); const a = anteilAm(z.start, z.end, tag); return a ? [a] : []; });
  const schule = schulBlock(occ, tag);
  return zusammenfassen(schule ? [...termine, { von: schule.von, bis: schule.bis }] : termine);
}

/**
 * Freie Fenster im Rahmen, ab Minute `ab` (heute: jetzt), mindestens `min`
 * Minuten lang.
 */
export function freieFenster(belegt: Intervall[], rahmen: Tagesrahmen, ab = 0, min = MIN_FENSTER): Intervall[] {
  const r = rahmenIntervall(rahmen);
  let t = Math.max(r.von, ab);
  const out: Intervall[] = [];
  for (const b of zusammenfassen(belegt)) {
    if (b.bis <= t) continue;
    if (b.von >= r.bis) break;
    if (b.von > t) out.push({ von: t, bis: Math.min(b.von, r.bis) });
    t = Math.max(t, b.bis);
  }
  if (t < r.bis) out.push({ von: t, bis: r.bis });
  return out.filter((f) => f.bis - f.von >= min);
}

/** Eine Zeile der Tages-Agenda. */
export type AgendaZeile =
  | { art: 'ganztags'; occ: CalendarOccurrence }
  | { art: 'termin'; occ: CalendarOccurrence; von: number; bis: number; vorbei: boolean; ausgefallen: boolean }
  | { art: 'schule'; von: number; bis: number; anzahl: number; vorbei: boolean }
  | { art: 'frei'; von: number; bis: number };

/**
 * Die Agenda eines Tages (F-50): ganztaegige Termine oben, dann Termine,
 * Schulblock und freie Fenster nach Uhrzeit. `jetzt` (Minute des Tages)
 * gibt es nur fuer heute - dann zaehlen freie Fenster erst ab jetzt, und
 * Vergangenes ist als `vorbei` markiert.
 */
export function agenda(occ: CalendarOccurrence[], tag: LocalDate, rahmen: Tagesrahmen, jetzt?: number): AgendaZeile[] {
  const ganztags = occ.filter((o) => o.allDay && datePart(String(o.start) as LocalDate) <= tag && datePart(String(o.end) as LocalDate) > tag)
    .sort((a, b) => a.title.localeCompare(b.title, 'de'))
    .map((o): AgendaZeile => ({ art: 'ganztags', occ: o }));

  const zeilen: Exclude<AgendaZeile, { art: 'ganztags' }>[] = [];
  for (const o of occ) {
    if (o.allDay || istSchulstunde(o)) continue;
    // Dieselbe Zeit wie in der Belegung - sonst klaffen bei "anders gelaufen" Luecken.
    const z = zeitVon(o);
    const a = anteilAm(z.start, z.end, tag);
    if (!a) continue;
    zeilen.push({ art: 'termin', occ: o, ...a, vorbei: jetzt !== undefined && a.bis <= jetzt, ausgefallen: o.bestaetigung?.status === 'ausgefallen' });
  }
  const schule = schulBlock(occ, tag);
  if (schule) zeilen.push({ art: 'schule', ...schule, vorbei: jetzt !== undefined && schule.bis <= jetzt });
  for (const f of freieFenster(belegtAm(occ, tag), rahmen, jetzt ?? 0)) zeilen.push({ art: 'frei', ...f });

  const rang = (z: (typeof zeilen)[number]) => (z.art === 'frei' ? 1 : 0);
  zeilen.sort((a, b) => a.von - b.von || rang(a) - rang(b) || a.bis - b.bis
    || (a.art === 'termin' && b.art === 'termin' ? a.occ.title.localeCompare(b.occ.title, 'de') : 0));
  return [...ganztags, ...zeilen];
}

/** "08:15" aus Minuten des Tages (1440 = "24:00"). */
export function uhrzeit(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

/** Laenge in Worten: "45 min", "1 h", "1 h 30 min". */
export function dauerText(min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
